"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function BlogFeedConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => <Card key={i} data-index={i} className="overflow-hidden border-border shadow-sm h-full flex flex-col">
            <AspectRatio ratio={16 / 9} className="bg-muted animate-pulse" />
            <CardContent className="p-6 flex-1 flex flex-col gap-4">
              <div className="h-5 w-24 bg-muted animate-pulse rounded" />
              <div className="h-6 w-full bg-muted animate-pulse rounded" />
              <div className="h-6 w-2/3 bg-muted animate-pulse rounded" />
              <div className="h-4 w-full bg-muted animate-pulse rounded mt-auto" />
              <div className="h-4 w-4/5 bg-muted animate-pulse rounded" />
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12 border border-dashed border-border rounded-xl bg-muted/30">
        <p className="text-muted-foreground">Unable to load latest insights at this time.</p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-center py-12 border border-dashed border-border rounded-xl bg-muted/30">
        <p className="text-muted-foreground">No insights published yet. Check back soon.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {posts.map((post, i) => <Card key={post.id} data-index={i} className="overflow-hidden border-border shadow-sm hover:shadow-md transition-all duration-300 h-full flex flex-col bg-card group">
          <Link to={post.link || "#"} className="block overflow-hidden">
            <AspectRatio ratio={16 / 9}>
              <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577832/img/generic_blog_thumbnail.png"} alt={post.title} className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" />
            </AspectRatio>
          </Link>
          <CardContent className="p-6 flex-1 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <Badge variant="secondary" className="bg-secondary/15 text-secondary-foreground hover:bg-secondary/25 border-none font-medium">
                {post.categories?.[0]?.name || "Policy Update"}
              </Badge>
              {post.date && <span className="text-xs text-muted-foreground font-medium">
                  {new Date(post.date).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}
                </span>}
            </div>
            
            <Link to={post.link || "#"} className="block mt-1">
              <h3 className="font-bold text-xl leading-tight text-foreground group-hover:text-primary transition-colors line-clamp-2">
                {post.title}
              </h3>
            </Link>
            
            <p className="text-muted-foreground text-sm line-clamp-2 mt-auto leading-relaxed">
              {post.excerpt ? post.excerpt.replace(/(<([^>]+)>)/gi, "") : "Explore the latest developments and strategic insights shaping the future of cross-border trade between Europe and India."}
            </p>
          </CardContent>
        </Card>)}
    </div>;
}
export default function EconomicSynergyInsights() {
  const wp_query_params = {
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="economic-synergy-insights" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-foreground tracking-tight">
              Latest Insights
            </h2>
            <p className="text-muted-foreground mt-4 text-lg">
              Expert analysis and updates on the evolving EU-India trade landscape.
            </p>
          </div>
          <div className="shrink-0">
            <Link to="#">
              <Button variant="outline" className="group">
                View All News
                <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>

        <PostsProvider wp_query={{
        per_page: 3,
        order: "desc",
        orderby: "date"
      }}>
          <BlogFeedConsumer />
        </PostsProvider>
      </div>
    </section>;
}